
import { Link } from "react-router-dom";
import { ArrowDown, BookOpen, Atom } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Convergence = () => {
  const intersections = [
    {
      title: "Philosophy of Science",
      description: "Where metaphysical inquiry meets empirical investigation",
      pillars: ["Philosophy", "Natural Sciences"],
      topics: ["Scientific Method", "Epistemology", "Nature of Reality", "Causation"]
    },
    {
      title: "Bioethics & Medical Philosophy", 
      description: "Exploring the moral dimensions of life, healing, and human enhancement",
      pillars: ["Philosophy", "Health Sciences"],
      topics: ["Medical Ethics", "End-of-life Care", "Genetic Engineering", "Consciousness"]
    },
    {
      title: "Mathematics & Theology",
      description: "The divine language of pattern, infinity, and transcendence",
      pillars: ["Mathematics", "Theology"],
      topics: ["Infinity Concepts", "Sacred Geometry", "Mathematical Beauty", "Divine Attributes"]
    },
    {
      title: "Technology & Society",
      description: "How engineering shapes human civilization and governance",
      pillars: ["Engineering", "History & Law"],
      topics: ["Digital Rights", "AI Governance", "Technological Revolution", "Future of Work"]
    },
    {
      title: "Environmental Law & Science",
      description: "Legal frameworks for planetary stewardship",
      pillars: ["Law", "Natural Sciences"],
      topics: ["Climate Policy", "Conservation Law", "Sustainability", "Environmental Justice"]
    },
    {
      title: "Mathematical Biology",
      description: "Quantifying the patterns and processes of life",
      pillars: ["Mathematics", "Life Sciences"],
      topics: ["Population Dynamics", "Epidemiology", "Bioinformatics", "Systems Biology"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-foreground hover:text-primary transition-colors">
            The Academy
          </Link>
          <nav className="flex space-x-6">
            <Link to="/human-inquiry" className="text-black hover:text-foreground transition-colors">
              The Human Inquiry
            </Link>
            <Link to="/world-itself" className="text-black hover:text-foreground transition-colors">
              The World Itself
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="flex justify-center items-center space-x-4 mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <div className="text-4xl font-bold text-black">⟷</div>
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Atom className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            Where Knowledge Converges
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            The two pillars do not stand in isolation. Here we explore the intersections, syntheses, and 
            collaborative inquiries that bridge soul and body, idea and artifact, law and nature, number and spirit.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Intersections */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-6">
            {intersections.map((intersection, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{intersection.title}</CardTitle>
                      <CardDescription className="text-base mb-3">
                        {intersection.description}
                      </CardDescription>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {intersection.pillars.map((pillar, i) => (
                          <span key={i} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                            {pillar}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 text-sm text-black uppercase tracking-wide">
                      Key Topics
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {intersection.topics.map((topic, i) => (
                        <span key={i} className="px-2 py-1 bg-muted text-black text-sm rounded">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Explore Intersection
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Begin Your Interdisciplinary Journey</h3>
          <p className="text-lg text-black mb-8">
            Knowledge finds its fullest expression not in isolation, but in the creative tension between domains. 
            Start exploring where your interests naturally converge.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/human-inquiry">Start with The Human Inquiry</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/world-itself">Start with The World Itself</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Convergence;
